import React, { useEffect, useState } from 'react';
import classes from './split-merge-modal.module.scss';

export type MergeGroupTable = {
  id: number;
  number: string;
  linkId: number;
};

type Props = {
  groupTables: MergeGroupTable[];
  onConfirm: (linkIdsToDeactivate: number[]) => void;
  onCancel: () => void;
  isLoading: boolean;
};

const Checkbox = ({ on }: { on: boolean }) => (
  <span className={`${classes.cb} ${on ? classes.cbOn : ''}`} aria-hidden="true">
    {on ? '✓' : ''}
  </span>
);

const SplitMergeModalComponent: React.FC<Props> = ({ groupTables, onConfirm, onCancel, isLoading }) => {
  const [selectedLinkIds, setSelectedLinkIds] = useState<Set<number>>(new Set());

  useEffect(() => {
    setSelectedLinkIds(new Set());
  }, [groupTables]);

  const toggle = (linkId: number) => {
    setSelectedLinkIds((prev) => {
      const next = new Set(prev);
      if (next.has(linkId)) {
        next.delete(linkId);
      } else {
        next.add(linkId);
      }
      return next;
    });
  };

  const allOn = selectedLinkIds.size === groupTables.length && groupTables.length > 0;

  const toggleAll = () => {
    if (allOn) {
      setSelectedLinkIds(new Set());
    } else {
      setSelectedLinkIds(new Set(groupTables.map((t) => t.linkId)));
    }
  };

  // Must leave at least one table in the merge group
  const canConfirm = selectedLinkIds.size > 0 && selectedLinkIds.size < groupTables.length && !isLoading;

  const handleConfirm = () => {
    if (!canConfirm) return;
    onConfirm([...selectedLinkIds]);
  };

  return (
    <div className={classes.overlay} role="dialog" aria-modal="true" aria-label="פיצול שולחנות" dir="rtl">
      <div className={classes.modal}>
        <header className={classes.header}>
          <span className={classes.title}>פיצול שולחנות</span>
          <button type="button" className={classes.closeBtn} onClick={onCancel} aria-label="סגור" data-testid="split-merge-close">
            ×
          </button>
        </header>

        <button type="button" className={`${classes.row} ${classes.rowAll}`} onClick={toggleAll} data-testid="split-merge-select-all">
          <span className={classes.itemName}>בחר הכל</span>
          <span className={classes.spacer} />
          <Checkbox on={allOn} />
        </button>

        <div className={classes.list}>
          {groupTables.map((table) => {
            const on = selectedLinkIds.has(table.linkId);
            return (
              <button
                key={table.id}
                type="button"
                className={`${classes.row} ${on ? classes.rowOn : ''}`}
                onClick={() => toggle(table.linkId)}
                aria-pressed={on}
                disabled={isLoading}
                data-testid={`split-merge-table-${table.id}`}
              >
                <span className={classes.tableIcon} aria-hidden="true">
                  <svg width="1em" height="1em" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <rect x="3" y="7" width="18" height="10" rx="2" />
                    <line x1="8" y1="7" x2="8" y2="5" />
                    <line x1="16" y1="7" x2="16" y2="5" />
                  </svg>
                </span>
                <span className={classes.itemName}>שולחן {table.number}</span>
                <span className={classes.spacer} />
                <Checkbox on={on} />
              </button>
            );
          })}
        </div>

        <footer className={classes.footer}>
          <div className={classes.sumLine}>
            <span className={`${classes.sum} ${selectedLinkIds.size > 0 ? classes.sumOn : ''}`}>
              {selectedLinkIds.size > 0 ? `${selectedLinkIds.size} שולחנות` : '—'}
            </span>
            <span className={classes.sumLabel}>
              {selectedLinkIds.size > 0
                ? selectedLinkIds.size < groupTables.length
                  ? 'יינותקו מהמיזוג'
                  : 'לא ניתן לנתק את כל השולחנות'
                : 'לא נבחרו שולחנות'}
            </span>
          </div>
          <div className={classes.btns}>
            <button type="button" className={`${classes.btn} ${classes.btnGhost}`} onClick={onCancel} disabled={isLoading} data-testid="split-merge-cancel">
              ביטול
            </button>
            <button
              type="button"
              className={`${classes.btn} ${classes.btnPrimary}`}
              disabled={!canConfirm}
              onClick={handleConfirm}
              data-testid="split-merge-confirm"
            >
              {isLoading ? 'מפצל...' : 'פיצול'}
            </button>
          </div>
        </footer>
      </div>
    </div>
  );
};

export const SplitMergeModal = React.memo(SplitMergeModalComponent);
